/**
 * Month/day date windows. A window carries no year, so it repeats every year and
 * may wrap the new year (e.g. Nov 1 → Feb 28).
 *
 * The comparison encodes a month/day pair as `month * 100 + day`, which orders
 * correctly within a year and makes the wrap case a simple OR. This is the same
 * scheme the Career Confidence check-in has always used; it lives here so the
 * check-in and plan availability share one implementation.
 */

export interface DateWindow {
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
}

/** A partial/unset window is not a window — every edge must be a real number. */
export const isCompleteWindow = (
  w: Partial<DateWindow> | null | undefined,
): w is DateWindow =>
  !!w &&
  typeof w.startMonth === "number" &&
  typeof w.startDay === "number" &&
  typeof w.endMonth === "number" &&
  typeof w.endDay === "number";

const encode = (month: number, day: number): number => month * 100 + day;

/** Days in `month` (1-indexed), using a non-leap year since we store no year. */
export const getDaysInMonth = (month: number): number =>
  new Date(2001, month, 0).getDate();

export const isWithinWindow = (
  window: Partial<DateWindow> | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!isCompleteWindow(window)) return false;

  const current = encode(now.getMonth() + 1, now.getDate());
  const start = encode(window.startMonth, window.startDay);
  const end = encode(window.endMonth, window.endDay);

  // A window that ends before it starts wraps through the new year.
  return start <= end
    ? current >= start && current <= end
    : current >= start || current <= end;
};

export const isWithinAnyWindow = (
  windows: (Partial<DateWindow> | null | undefined)[] | null | undefined,
  now: Date = new Date(),
): boolean => (windows ?? []).some((w) => isWithinWindow(w, now));

/**
 * The next date a window opens, or null when there are no usable windows.
 * Returns today when a window is already open, so callers can treat a non-null
 * result as "the date this becomes available".
 */
export const nextWindowStart = (
  windows: (Partial<DateWindow> | null | undefined)[] | null | undefined,
  now: Date = new Date(),
): Date | null => {
  const usable = (windows ?? []).filter(isCompleteWindow);
  if (usable.length === 0) return null;
  if (isWithinAnyWindow(usable, now)) return now;

  const year = now.getFullYear();
  const candidates = usable.flatMap((w) => [
    new Date(year, w.startMonth - 1, w.startDay),
    // A window whose start has already passed this year opens again next year.
    new Date(year + 1, w.startMonth - 1, w.startDay),
  ]);

  const today = new Date(year, now.getMonth(), now.getDate());
  const upcoming = candidates
    .filter((d) => d.getTime() >= today.getTime())
    .sort((a, b) => a.getTime() - b.getTime());

  return upcoming[0] ?? null;
};

/** "March 1" — the caption shown when planning is closed. */
export const formatWindowStart = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(
    date,
  );
